import React, { useEffect, useState } from "react";
import axios from "utils/axiosConfig";
import { useSelector } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import {
  HiOutlineUserGroup,
  HiOutlineCube,
  HiOutlineCurrencyDollar,
  HiOutlineTrendingUp,
} from "react-icons/hi";

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${uri}/api/admin/stats`, {
          headers: { "x-user-email": user?.data?.email },
        });
        setStats(response.data.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.data?.email) fetchStats();
  }, [user]);

  const statCards = [
    {
      title: "Active Players",
      value: stats?.totalUsers || 0,
      icon: <HiOutlineUserGroup size={24} />,
      color: "from-cyan-500 to-blue-600",
      shadow: "shadow-cyan-500/20",
      text: "text-cyan-400",
    },
    {
      title: "Hardare Deployments",
      value: stats?.totalOrders || 0,
      icon: <HiOutlineCube size={24} />,
      color: "from-purple-500 to-indigo-600",
      shadow: "shadow-purple-500/20",
      text: "text-purple-400",
    },
    {
      title: "Tactical Revenue",
      value: `$${stats?.totalRevenue?.toLocaleString() || 0}`,
      icon: <HiOutlineCurrencyDollar size={24} />,
      color: "from-emerald-500 to-green-600",
      shadow: "shadow-emerald-500/20",
      text: "text-emerald-400",
    },
    {
      title: "Credits Circulating",
      value: stats?.totalCredits?.toLocaleString() || 0,
      icon: <HiOutlineTrendingUp size={24} />,
      color: "from-amber-500 to-orange-600",
      shadow: "shadow-amber-500/20",
      text: "text-amber-400",
    },
  ];

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-6">
        <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />
        <p className="text-cyan-400 font-black uppercase tracking-[0.3em] italic text-xs animate-pulse">
          Synchronizing_System_Metrics...
        </p>
      </div>
    );

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest mb-4">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping" />
            System_Status: Operational
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
            Command <span className="text-cyan-400">Overview</span>
          </h1>
          <p className="mt-2 text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
            Centralized intelligence and platform-wide performance diagnostics.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="group relative bg-white/5 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/10 hover:border-white/20 transition-all duration-500 overflow-hidden"
            >
              {/* Background Glow */}
              <div
                className={`absolute -right-4 -top-4 w-24 h-24 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-10 blur-2xl transition-opacity duration-500`}
              />

              <div className="relative z-10">
                <div
                  className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${card.color} text-white shadow-2xl ${card.shadow} transform group-hover:scale-110 transition-transform duration-500`}
                >
                  {card.icon}
                </div>

                <div className="mt-8">
                  <p className="text-[10px] font-black text-gray-500 tracking-[0.2em] mb-2 uppercase italic transition-colors group-hover:text-gray-400">
                    {card.title}
                  </p>
                  <h3 className="text-3xl font-black text-white tracking-tighter italic">
                    {card.value}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity Log */}
        <div className="bg-white/5 backdrop-blur-xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
          <div className="p-8 border-b border-white/5 bg-black/20 flex items-center justify-between">
            <h2 className="text-sm font-black text-white px-4 py-2 bg-white/5 rounded-xl border border-white/10 uppercase tracking-[0.3em] italic">
              Recent System Activity{" "}
              <span className="text-cyan-400 ml-2">Pulse</span>
            </h2>
            <div className="flex gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <div className="w-2 h-2 rounded-full bg-green-500" />
            </div>
          </div>
          <div className="p-8">
            <div className="space-y-4">
              {[
                {
                  event: "Inventory_Sync_Completed",
                  detail: "Hardware catalog integrity verified successfully.",
                  time: "2m ago",
                  color: "text-cyan-400",
                },
                {
                  event: "New_Recruit_Registration",
                  detail:
                    "Player identity authenticated and credit bonus assigned.",
                  time: "15m ago",
                  color: "text-purple-400",
                },
                {
                  event: "Deployment_Protocol_Initiated",
                  detail:
                    "New order received and queued for tactical dispatch.",
                  time: "1h ago",
                  color: "text-emerald-400",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 p-6 hover:bg-white/[0.02] rounded-[2rem] border border-transparent hover:border-white/5 transition-all group"
                >
                  <div
                    className={`h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center ${item.color} font-black text-lg shadow-inner group-hover:scale-110 transition-transform`}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-xs font-black uppercase tracking-widest italic ${item.color}`}
                    >
                      {item.event}
                    </p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-1">
                      {item.detail}
                    </p>
                  </div>
                  <span className="text-[10px] font-black text-gray-600 uppercase tracking-tighter">
                    {item.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 bg-black/40 border-t border-white/5 text-center">
            <button className="text-[10px] font-black text-gray-500 hover:text-cyan-400 uppercase tracking-[0.4em] transition-colors py-2">
              Access Full Diagnostic Logs
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
